
import React from 'react';
import { Key, UserCog, Shield, Settings as SettingsIcon, LineChart, Rss } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import ApiKeyForm from '@/components/settings/ApiKeyForm';
import ApiKeyGuide from '@/components/settings/ApiKeyGuide';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import YouTubeQuotaInfo from '@/components/dashboard/YouTubeQuotaInfo';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import QuotaUsageDisplay from '@/components/settings/QuotaUsageDisplay';
import VideoSourceSettings from '@/components/settings/VideoSourceSettings';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useState, useEffect } from 'react';

const Settings = () => {
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [videoSource, setVideoSource] = useState('rss');
  const [hasApiKey, setHasApiKey] = useState(false);

  // Fetch user preferences
  useEffect(() => {
    const fetchUserPreferences = async () => {
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('video_source, youtube_api_key')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setVideoSource(data.video_source || 'rss');
          setHasApiKey(!!data.youtube_api_key);
        }
      }
    };

    fetchUserPreferences();
  }, [user]);

  const handleVideoSourceChange = async (newSource: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ video_source: newSource })
        .eq('id', user.id);

      if (error) throw error;

      setVideoSource(newSource);
      toast({
        title: "Préférences mises à jour",
        description: `Source des vidéos changée vers ${newSource === 'rss' ? 'RSS' : newSource === 'invidious' ? 'Invidious' : 'YouTube API'}`
      });
    } catch (error) {
      console.error('Error updating video source:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour les préférences",
        variant: "destructive"
      });
    }
  };

  // Rediriger vers la page de connexion si l'utilisateur n'est pas connecté
  if (!loading && !user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <header className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <UserCog className="w-7 h-7 md:w-8 md:h-8 text-youtube-red" />
            Paramètres du compte
          </h1>
          <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 mt-2">
            Personnalisez votre expérience et gérez vos préférences
          </p>
        </header>

        <Tabs defaultValue="apikey" className="w-full">
          <TabsList className="mb-6 w-full md:w-auto overflow-x-auto flex-nowrap">
            <TabsTrigger value="apikey" className="flex items-center gap-2 whitespace-nowrap">
              <Key className="w-4 h-4" />
              <span className={isMobile ? "text-sm" : ""}>Clé API YouTube</span>
            </TabsTrigger>
            <TabsTrigger value="quotas" className="flex items-center gap-2 whitespace-nowrap">
              <LineChart className="w-4 h-4" />
              <span className={isMobile ? "text-sm" : ""}>Quotas</span>
            </TabsTrigger>
            <TabsTrigger value="sources" className="flex items-center gap-2 whitespace-nowrap">
              <Rss className="w-4 h-4" />
              <span className={isMobile ? "text-sm" : ""}>Sources vidéos</span>
            </TabsTrigger>
            <TabsTrigger value="general" className="flex items-center gap-2 whitespace-nowrap">
              <SettingsIcon className="w-4 h-4" />
              <span className={isMobile ? "text-sm" : ""}>Général</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="apikey" className="space-y-6">
            <Alert variant="default" className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <Key className="h-5 w-5 text-blue-500" />
              <AlertTitle className="text-blue-700 dark:text-blue-400">Clé API YouTube requise</AlertTitle>
              <AlertDescription className="text-blue-600 dark:text-blue-300 text-sm md:text-base">
                Pour utiliser cette application, vous devez fournir votre propre clé API YouTube. Cela vous permettra d'accéder aux fonctionnalités de l'API YouTube avec votre propre quota.
              </AlertDescription>
            </Alert>
            
            {user && <YouTubeQuotaInfo className="mb-6" />}
            
            <ApiKeyForm />
            <ApiKeyGuide />
          </TabsContent>
          
          <TabsContent value="quotas" className="space-y-6">
            <h2 className="text-lg md:text-xl font-semibold mb-4 flex items-center gap-2">
              <LineChart className="w-5 h-5 text-youtube-red" />
              Utilisation des quotas API
            </h2>
            
            <div className="grid gap-6">
              <QuotaUsageDisplay />
              
              <Alert variant="default" className="bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800">
                <Shield className="h-5 w-5 text-gray-500" />
                <AlertTitle className="text-gray-700 dark:text-gray-300">Optimisation des quotas</AlertTitle>
                <AlertDescription className="text-gray-600 dark:text-gray-400 text-sm md:text-base">
                  Cette application utilise maintenant un système de cache avancé pour réduire l'utilisation des quotas API YouTube. Les vidéos sont mises en cache jusqu'à 7 jours, vous permettant d'accéder à plus de contenu sans dépasser vos limites quotidiennes.
                </AlertDescription>
              </Alert>
            </div>
          </TabsContent>
          
          <TabsContent value="sources" className="space-y-6">
            <h2 className="text-lg md:text-xl font-semibold mb-4 flex items-center gap-2">
              <Rss className="w-5 h-5 text-youtube-red" />
              Sources des vidéos
            </h2>
            
            <VideoSourceSettings 
              currentSource={videoSource}
              onSourceChange={handleVideoSourceChange}
              hasApiKey={hasApiKey}
            />
          </TabsContent>
          
          <TabsContent value="general" className="space-y-6">
            <h2 className="text-lg md:text-xl font-semibold mb-4 flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-youtube-red" />
              Paramètres généraux
            </h2>
            
            <p className="text-gray-500 dark:text-gray-400 text-sm md:text-base">
              D'autres paramètres seront disponibles prochainement.
            </p>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Settings;
